import React, { useEffect } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.heat';
import CategoryTreePanel from '../components/shared/CategoryTreePanel';
import CustomMapStyling from '../components/Map/CustomMapStyling';
import { PieChartMarkers, getQualityBand, QUALITY_COLORS, DOC_TYPE_COLORS } from '../components/Map/PieChartMarkers';
import IndividualDotLayer from '../components/Map/IndividualDotLayer';
import FractalClusterLayer from '../components/Map/FractalClusterLayer';
import VoronoiLayer from '../components/Map/VoronoiLayer';
import SocialDiscoveryBanner from '../components/shared/SocialDiscoveryBanner';
import UltraDenseControlPanel from '../components/UltimateSearch/UltraDenseControlPanel';
import PowerUserControlPanel from '../components/common/PowerUserControlPanel';
import MapHoverPopup from '../components/Map/MapHoverPopup';
import MapStatsPanel from '../components/Map/MapStatsPanel';
import MapVizControls from '../components/Map/MapVizControls';
import MapExportSection from '../components/Map/MapExportSection';
import MapAnalyticsSection from '../components/Map/MapAnalyticsSection';
import MapResultsList from '../components/Map/MapResultsList';
import MapSearchPanel from '../components/Map/MapSearchPanel';
import MapFilterPanels from '../components/Map/MapFilterPanels';
import MapColorLegend from '../components/Map/MapColorLegend';
import useMapPageData, { CATEGORY_COLORS } from '../hooks/useMapPageData';
import { SmallBookAd, MediumBookAd, BannerBookAd } from '../components/Ads/BookAdvertisements';

// Heatmap layer component using leaflet.heat
const HeatmapLayer = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const heat = L.heatLayer(points, {
      radius: 25, blur: 20, maxZoom: 10,
      gradient: { 0.2: '#2563eb', 0.4: '#FCD34D', 0.6: '#c026d3', 0.8: '#f59e0b', 1.0: '#ef4444' }
    }).addTo(map);
    return () => { map.removeLayer(heat); };
  }, [map, points]);
  return null;
};

const MapPage = ({ showToast, setCurrentPage }) => {
  const data = useMapPageData(showToast);
  const {
    token, user, filters,
    educatedIndividualFilters, setEducatedIndividualFilters,
    eiMinWords, setEiMinWords,
    minWordCount, setMinWordCount,
    documentTypes, selectedDocTypes, setSelectedDocTypes,
    docTypeMinWords, setDocTypeMinWords,
    certificationFilters, setCertificationFilters,
    certMinWords, setCertMinWords,
    contentMinWords, setContentMinWords,
    minQualityScore, setMinQualityScore,
    exactMatch, setExactMatch,
    searchDepth, setSearchDepth,
    aggregation, setAggregation,
    selectedCategories, setSelectedCategories,
    showControlPanel, setShowControlPanel,
    controlPanelLayout, setControlPanelLayout,
    filterHistory, getRecommendations,
    loadPreferences, exportFilters,
    undo, redo, canUndo, canRedo,
    mapResults, loading, mapCollapsed, setMapCollapsed,
    showProgrammerMode, setShowProgrammerMode,
    hoveredResult, hoverPosition, autoRefresh, setAutoRefresh,
    lastUpdate, containerWidth, showMapStyling, setShowMapStyling,
    vizModeAdvanced, setVizModeAdvanced, dotColorMode, setDotColorMode,
    legendFilter, setLegendFilter, mapStyle, setMapStyle,
    mapContainerRef, vizMode, setVizMode,
    activeDatasets, activeModes, toggleDataset,
    dualMapView, setDualMapView, comparisonVizRight, setComparisonVizRight,
    mapUrlLimit, setMapUrlLimit, windowOverlay, setWindowOverlay,
    plottedWindows,
    dataSource, setDataSource, showAnalytics, setShowAnalytics,
    categories, showCategoryFilter, setShowCategoryFilter, categoryColorMap,
    toggleCategory, selectedCategoryNames,
    filteredMapResults, effectiveMapResults, legendFilteredResults,
    getResultCategoryColor, getMarkerColor,
    aiSearchQuery, setAiSearchQuery, aiSearchLoading, aiSearchMode, setAiSearchMode,
    dbSearchLoading, dbSearchMode, setDbSearchMode,
    seaLoading, seaResults, searchPreferences, toggleSearchPreference,
    fetchCategories, fetchMapResults, exportMapData,
    aiSearchForMap, autoCategorizeSearc, databaseSearchForMap, searchEngineAccessForMap,
    refreshingDots, refreshProgress, totalMapped, totalResults,
    cleaningDots, clearingDots, cleanProgress,
    cleanMapDots, clearAllDots, refreshMapDots,
    isPopupMaximized, handleMarkerClick, handleMarkerHover, handleMarkerLeave,
    handlePopupMouseEnter, handlePopupMouseLeave, closePopup, togglePopupMaximize,
  } = data;

  return (
    <div className="card" data-testid="map-page">
      {/* Ultra-Dense Control Panel (shared via FilterContext) */}
      <UltraDenseControlPanel
        show={showControlPanel} onClose={() => setShowControlPanel(false)}
        layout={controlPanelLayout} setLayout={setControlPanelLayout}
        educatedIndividualFilters={educatedIndividualFilters} setEducatedIndividualFilters={setEducatedIndividualFilters}
        eiMinWords={eiMinWords} setEiMinWords={setEiMinWords}
        selectedDocTypes={selectedDocTypes} setSelectedDocTypes={setSelectedDocTypes} documentTypes={documentTypes}
        docTypeMinWords={docTypeMinWords} setDocTypeMinWords={setDocTypeMinWords}
        certificationFilters={certificationFilters} setCertificationFilters={setCertificationFilters}
        certMinWords={certMinWords} setCertMinWords={setCertMinWords}
        minQualityScore={minQualityScore} setMinQualityScore={setMinQualityScore}
        minWordCount={minWordCount} setMinWordCount={setMinWordCount}
        exactMatch={exactMatch} setExactMatch={setExactMatch}
        searchDepth={searchDepth} setSearchDepth={setSearchDepth}
        aggregation={aggregation} setAggregation={setAggregation}
        selectedCategories={selectedCategories} setSelectedCategories={setSelectedCategories} categories={categories}
        contentMinWords={contentMinWords} setContentMinWords={setContentMinWords}
        filterHistory={filterHistory} getRecommendations={getRecommendations}
        showToast={showToast}
      />
      <PowerUserControlPanel show={showProgrammerMode} onClose={() => setShowProgrammerMode(false)} />
      {/* Social Discovery Banner */}
      <SocialDiscoveryBanner variant="compact" />
      <div className="card-header">
        <h2>🗺️ Interactive World Map</h2>
        <p style={{ color: '#047857', fontSize: '0.72rem', letterSpacing: 1, margin: '2px 0 0', fontWeight: 500 }}>Advanced Research Platform &middot; Information Exchange Network</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap' }}>
          {/* Control Panel Toggle */}
          <button onClick={() => setShowControlPanel(true)} data-testid="map-open-control-panel-btn"
            className="btn-ocean ocean-shimmer"
            style={{ background: 'linear-gradient(135deg, rgba(253, 230, 138,0.12), rgba(252, 211, 77,0.08))', border: '1px solid rgba(253, 230, 138,0.3)', color: '#38bdf8', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace', letterSpacing: 0.8, position: 'relative', overflow: 'hidden' }}>
            [CTRL]
          </button>
          <button onClick={() => setShowProgrammerMode(true)} data-testid="map-open-programmer-mode-btn"
            className="btn-ocean"
            style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(253, 230, 138,0.08))', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace', letterSpacing: 0.8 }}>
            [PRO]
          </button>
          <button onClick={undo} disabled={!canUndo} data-testid="map-undo-btn" title="Undo"
            style={{ background: canUndo ? 'rgba(245,158,11,0.12)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${canUndo ? 'rgba(245,158,11,0.3)' : 'rgba(203, 213, 225, 0.8)'}`, color: canUndo ? '#f59e0b' : '#555', padding: '7px 10px', borderRadius: 8, cursor: canUndo ? 'pointer' : 'default', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace' }}>
            <i className="fas fa-undo" />
          </button>
          <button onClick={redo} disabled={!canRedo} data-testid="map-redo-btn" title="Redo"
            style={{ background: canRedo ? 'rgba(16,185,129,0.12)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${canRedo ? 'rgba(16,185,129,0.3)' : 'rgba(203, 213, 225, 0.8)'}`, color: canRedo ? '#10b981' : '#555', padding: '7px 10px', borderRadius: 8, cursor: canRedo ? 'pointer' : 'default', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace' }}>
            <i className="fas fa-redo" />
          </button>
          <button onClick={() => setMapCollapsed(v => !v)} data-testid="map-collapse-btn"
            style={{ background: mapCollapsed ? 'rgba(244,114,182,0.15)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${mapCollapsed ? 'rgba(244,114,182,0.3)' : 'rgba(255,255,255,0.1)'}`, color: mapCollapsed ? '#FEF08A' : '#a1a1aa', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 600 }}>
            <i className={`fas fa-${mapCollapsed ? 'expand' : 'compress'}`} style={{ marginRight: 4 }} />
            {mapCollapsed ? 'Expand Map' : 'Collapse Map'}
          </button>
          {/* Data Source Toggle */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            background: '#334155', 
            borderRadius: 20, 
            padding: '3px',
            border: '1px solid rgba(252, 211, 77, 0.3)'
          }} data-testid="data-source-toggle">
            <button
              onClick={() => { setDataSource('worldwide'); }}
              style={{
                padding: '6px 14px',
                borderRadius: 18,
                border: 'none',
                background: dataSource === 'worldwide' 
                  ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                  : 'transparent',
                color: dataSource === 'worldwide' ? '#fff' : '#a1a1aa',
                fontWeight: dataSource === 'worldwide' ? 600 : 400,
                fontSize: '0.8rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              data-testid="data-source-worldwide"
            >
              🌍 Worldwide
            </button>
            <button
              onClick={() => { setDataSource('personal'); }}
              style={{
                padding: '6px 14px',
                borderRadius: 18,
                border: 'none',
                background: dataSource === 'personal' 
                  ? 'linear-gradient(135deg, #10b981, #059669)' 
                  : 'transparent',
                color: dataSource === 'personal' ? '#fff' : '#a1a1aa',
                fontWeight: dataSource === 'personal' ? 600 : 400,
                fontSize: '0.8rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              data-testid="data-source-personal"
            >
              👤 My Data
            </button>
          </div>
          
          <span style={{ color: '#10b981', fontSize: '0.9rem' }}>
            {mapResults.length} results mapped • Click markers to open articles
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button 
              onClick={() => fetchMapResults(true)} 
              className="btn btn-secondary"
              style={{ padding: '4px 12px', fontSize: '0.8rem' }}
              data-testid="map-refresh-btn"
            >
              🔄 Refresh
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '0.8rem', color: '#a1a1aa' }}>
              <input 
                type="checkbox" 
                checked={autoRefresh} 
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ accentColor: '#10b981' }}
              />
              Auto-refresh
            </label>
            {lastUpdate && (
              <span style={{ fontSize: '0.75rem', color: '#71717a' }}>
                Last: {lastUpdate.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => setShowMapStyling(!showMapStyling)}
              className="btn btn-secondary"
              style={{ 
                padding: '4px 12px', 
                fontSize: '0.8rem',
                background: showMapStyling ? 'linear-gradient(135deg, #FCD34D, #3b82f6)' : undefined
              }}
              data-testid="toggle-map-styling-btn"
            >
              🎨 {showMapStyling ? 'Hide Styling' : 'Map Style'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Custom Map Styling Panel */}
      {showMapStyling && (
        <div style={{ marginBottom: 15 }}>
          <CustomMapStyling 
            onStyleChange={(style) => {
              setMapStyle(style);
              localStorage.setItem('mapStylePreferences', JSON.stringify(style));
            }}
            currentStyle={mapStyle}
            showToast={showToast}
          />
        </div>
      )}
      
      {/* AI Search Section */}
      <MapSearchPanel
        aiSearchQuery={aiSearchQuery}
        setAiSearchQuery={setAiSearchQuery}
        onAISearch={aiSearchForMap}
        onAutoCategorize={autoCategorizeSearc}
        onDatabaseSearch={databaseSearchForMap}
        onSearchEngineAccess={searchEngineAccessForMap}
        aiSearchLoading={aiSearchLoading}
        dbSearchLoading={dbSearchLoading}
        seaLoading={seaLoading}
        aiSearchMode={aiSearchMode}
        setAiSearchMode={setAiSearchMode}
        dbSearchMode={dbSearchMode}
        setDbSearchMode={setDbSearchMode}
        searchPreferences={searchPreferences}
        toggleSearchPreference={toggleSearchPreference}
      />

      {/* Category, Educated Individual, Document Type, and Certification Filters */}
      <MapFilterPanels
        categories={categories}
        selectedCategories={selectedCategories}
        toggleCategory={toggleCategory}
        setSelectedCategories={setSelectedCategories}
        categoryColorMap={categoryColorMap}
        mapResults={mapResults}
        filteredMapResults={filteredMapResults}
        showCategoryFilter={showCategoryFilter}
        setShowCategoryFilter={setShowCategoryFilter}
        educatedIndividualFilters={educatedIndividualFilters}
        setEducatedIndividualFilters={setEducatedIndividualFilters}
        selectedDocTypes={selectedDocTypes}
        setSelectedDocTypes={setSelectedDocTypes}
        documentTypes={documentTypes}
        certificationFilters={certificationFilters}
        setCertificationFilters={setCertificationFilters}
      />

      {/* Advanced Visualization Controls */}
      <MapVizControls
        vizModeAdvanced={vizModeAdvanced}
        setVizModeAdvanced={setVizModeAdvanced}
        activeDatasets={activeDatasets}
        toggleDataset={toggleDataset}
        dualMapView={dualMapView}
        setDualMapView={setDualMapView}
        mapUrlLimit={mapUrlLimit}
        setMapUrlLimit={setMapUrlLimit}
        windowOverlay={windowOverlay}
        setWindowOverlay={setWindowOverlay}
        comparisonVizRight={comparisonVizRight}
        setComparisonVizRight={setComparisonVizRight}
        dotColorMode={dotColorMode}
        setDotColorMode={setDotColorMode}
      />

      {/* Dynamic Legend — shows all active datasets */}
      <div style={{ 
        display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 15,
        padding: 10, background: '#475569', borderRadius: 10
      }}>
        {activeDatasets.category && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: '#047857', fontSize: '0.68rem', fontWeight: 700, marginRight: 2 }}>CAT:</span>
            {categories.slice(0, 8).map((cat) => (
              <div key={cat.name} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', opacity: selectedCategories.length === 0 || selectedCategories.includes(cat.id || cat._id) ? 1 : 0.4 }}
                onClick={() => toggleCategory(cat.id || cat._id)}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: categoryColorMap[cat.name] || '#6b7280' }} />
                <span style={{ fontSize: '0.68rem', color: '#a1a1aa' }}>{cat.name?.substring(0, 16)}</span>
              </div>
            ))}
          </div>
        )}
        {activeDatasets.doctype && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: '#f59e0b', fontSize: '0.68rem', fontWeight: 700, marginRight: 2 }}>DOC:</span>
            {Object.entries(DOC_TYPE_COLORS).slice(0, 7).map(([type, color]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
                <span style={{ fontSize: '0.68rem', color: '#a1a1aa' }}>{type}</span>
              </div>
            ))}
          </div>
        )}
        {activeDatasets.quality && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: '#10b981', fontSize: '0.68rem', fontWeight: 700, marginRight: 2 }}>QUALITY:</span>
            {Object.entries(QUALITY_COLORS).map(([band, color]) => (
              <div key={band} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
                <span style={{ fontSize: '0.68rem', color: '#a1a1aa' }}>{band}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <div className="loading-spinner"><div className="spinner"></div></div>
          <p style={{ color: '#a1a1aa', marginTop: 15 }}>Loading map data...</p>
        </div>
      ) : mapResults.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <p style={{ color: '#a1a1aa', fontSize: '1.1rem' }}>
            No mapped results yet. Search and categorize content to see results on the map!
          </p>
          <button className="btn btn-primary" style={{ marginTop: 15 }} onClick={() => setCurrentPage('search')}>
            Go to Ultimate Search
          </button>
        </div>
      ) : (
        <>
          {/* React-Leaflet Map with hover popups */}
          {!mapCollapsed && (
          <div style={{ display: 'flex', gap: dualMapView ? 10 : 20 }}>
          {/* Primary Map (consolidated clusters or single full map) */}
          <div 
            ref={mapContainerRef}
            style={{ 
              width: dualMapView ? '50%' : '59.26%',
              height: 450, borderRadius: 12, overflow: 'hidden',
              border: '2px solid rgba(252, 211, 77, 0.3)',
              position: 'relative',
              flexShrink: 0
            }}
          >
            {dualMapView && (
              <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 500, background: 'rgba(253, 251, 247,0.85)', padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(252, 211, 77,0.3)' }}>
                <span style={{ color: '#047857', fontSize: '0.68rem', fontWeight: 700 }}>Consolidated View</span>
              </div>
            )}
            <MapContainer
              center={[39.8283, -98.5795]}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url={mapStyle.tileUrl || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
              />
              {vizModeAdvanced === 'heatmap' && effectiveMapResults.length > 0 && (
                <HeatmapLayer points={effectiveMapResults.slice(0, 500).map(r => [r.latitude, r.longitude, 0.8])} />
              )}
              {vizModeAdvanced === 'individual' && (
                <IndividualDotLayer
                  results={legendFilteredResults}
                  colorMode={dotColorMode}
                  categoryColorMap={categoryColorMap}
                  onMarkerClick={handleMarkerClick}
                  onMarkerHover={handleMarkerHover}
                  onMarkerLeave={handleMarkerLeave}
                />
              )}
              {vizModeAdvanced === 'standard' && (
                <PieChartMarkers
                  results={effectiveMapResults}
                  activeModes={activeModes}
                  categoryColorMap={categoryColorMap}
                  onMarkerClick={handleMarkerClick}
                  onMarkerHover={handleMarkerHover}
                  onMarkerLeave={handleMarkerLeave}
                  granularMode={false}
                />
              )}
              {vizModeAdvanced === 'fractal' && (
                <FractalClusterLayer
                  results={effectiveMapResults}
                  onMarkerClick={handleMarkerClick}
                  onMarkerHover={handleMarkerHover}
                  onMarkerLeave={handleMarkerLeave}
                />
              )}
              {vizModeAdvanced === 'voronoi' && (
                <VoronoiLayer
                  results={effectiveMapResults}
                  categoryColorMap={categoryColorMap}
                  onMarkerClick={handleMarkerClick}
                  onMarkerHover={handleMarkerHover}
                  onMarkerLeave={handleMarkerLeave}
                />
              )}
            </MapContainer>

            {/* Color Legend — Individual Dots mode */}
            {vizModeAdvanced === 'individual' && (
              <MapColorLegend
                results={effectiveMapResults}
                colorMode={dotColorMode}
                categoryColorMap={categoryColorMap}
                onFilterByItem={setLegendFilter}
                activeFilter={legendFilter}
              />
            )}

            {/* Enhanced Hover Popup Window */}
            <MapHoverPopup
              hoveredResult={hoveredResult}
              hoverPosition={hoverPosition}
              containerWidth={containerWidth}
              isPopupMaximized={isPopupMaximized}
              getMarkerColor={getMarkerColor}
              categoryColorMap={categoryColorMap}
              onMouseEnter={handlePopupMouseEnter}
              onMouseLeave={handlePopupMouseLeave}
              onToggleMaximize={togglePopupMaximize}
              onClose={closePopup}
              onMarkerClick={handleMarkerClick}
            />
          </div>
          
          {/* Second Map — Comparison view with independent visualization (only in dual mode) */}
          {dualMapView && (
            <div style={{
              width: '50%', height: 450, borderRadius: 12, overflow: 'hidden',
              border: '2px solid rgba(16,185,129,0.3)', position: 'relative', flexShrink: 0
            }}>
              <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 500, background: 'rgba(253, 251, 247,0.85)', padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(16,185,129,0.3)' }}>
                <span style={{ color: '#10b981', fontSize: '0.68rem', fontWeight: 700 }}>
                  {comparisonVizRight.charAt(0).toUpperCase() + comparisonVizRight.slice(1)} View
                </span>
              </div>
              <MapContainer
                center={[39.8283, -98.5795]}
                zoom={4}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; OSM'
                  url={mapStyle.tileUrl || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
                />
                {comparisonVizRight === 'heatmap' && effectiveMapResults.length > 0 && (
                  <HeatmapLayer points={effectiveMapResults.slice(0, 500).map(r => [r.latitude, r.longitude, 0.8])} />
                )}
                {comparisonVizRight === 'individual' && (
                  <IndividualDotLayer
                    results={legendFilteredResults}
                    colorMode={dotColorMode}
                    categoryColorMap={categoryColorMap}
                    onMarkerClick={handleMarkerClick}
                    onMarkerHover={handleMarkerHover}
                    onMarkerLeave={handleMarkerLeave}
                  />
                )}
                {comparisonVizRight === 'standard' && (
                  <PieChartMarkers
                    results={effectiveMapResults}
                    activeModes={activeModes}
                    categoryColorMap={categoryColorMap}
                    onMarkerClick={handleMarkerClick}
                    onMarkerHover={handleMarkerHover}
                    onMarkerLeave={handleMarkerLeave}
                    granularMode={true}
                  />
                )}
                {comparisonVizRight === 'fractal' && (
                  <FractalClusterLayer
                    results={effectiveMapResults}
                    onMarkerClick={handleMarkerClick}
                    onMarkerHover={handleMarkerHover}
                    onMarkerLeave={handleMarkerLeave}
                  />
                )}
                {comparisonVizRight === 'voronoi' && (
                  <VoronoiLayer
                    results={effectiveMapResults}
                    categoryColorMap={categoryColorMap}
                    onMarkerClick={handleMarkerClick}
                    onMarkerHover={handleMarkerHover}
                    onMarkerLeave={handleMarkerLeave}
                  />
                )}
              </MapContainer>
              {/* Color Legend — right pane Individual Dots mode */}
              {comparisonVizRight === 'individual' && (
                <MapColorLegend
                  results={effectiveMapResults}
                  colorMode={dotColorMode}
                  categoryColorMap={categoryColorMap}
                  onFilterByItem={setLegendFilter}
                  activeFilter={legendFilter}
                />
              )}
            </div>
          )}
          
          {/* Right side panel - Map Statistics (hidden in dual map mode) */}
          {!dualMapView && (
          <MapStatsPanel
            filteredMapResults={filteredMapResults}
            totalResults={totalResults}
            mapResults={mapResults}
            categories={categories}
            selectedDocTypes={selectedDocTypes}
            documentTypes={documentTypes}
            refreshMapDots={refreshMapDots}
            refreshingDots={refreshingDots}
            refreshProgress={refreshProgress}
            cleanMapDots={cleanMapDots}
            cleaningDots={cleaningDots}
            clearAllDots={clearAllDots}
            clearingDots={clearingDots}
            cleanProgress={cleanProgress}
            dataSource={dataSource}
            lastUpdate={lastUpdate}
          />
          )}
          </div>
          )}
          {mapCollapsed && (
            <div style={{ padding: '30px 0', textAlign: 'center', color: '#71717a', fontSize: '0.85rem', background: 'rgba(255, 255, 255,0.3)', borderRadius: 12, border: '1px dashed rgba(244,114,182,0.2)' }}
              data-testid="map-collapsed-placeholder">
              <i className="fas fa-map" style={{ fontSize: '2rem', marginBottom: 8, display: 'block', color: '#047857', opacity: 0.4 }} />
              Map collapsed. Click "Expand Map" above to show. <strong>{effectiveMapResults.length}</strong> locations available.
            </div>
          )}

          {/* Categories & Sub-Categories below Map */}
          <CategoryTreePanel
            token={token}
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryClick={(catId) => {
              setSelectedCategories(prev =>
                prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
              );
            }}
          />

          {/* Results Lists */}
          <MapResultsList
            mapResults={mapResults}
            filteredMapResults={filteredMapResults}
            selectedCategories={selectedCategories}
            selectedCategoryNames={selectedCategoryNames}
            categoryColorMap={categoryColorMap}
            getMarkerColor={getMarkerColor}
            getResultCategoryColor={getResultCategoryColor}
          />
        </>
      )}

      {/* Premium Map Export Section */}
      <MapExportSection mapResults={mapResults} exportMapData={exportMapData} />

      {/* Advanced Analytics Dashboard */}
      <MapAnalyticsSection
        mapResults={mapResults}
        categories={categories}
        showAnalytics={showAnalytics}
        setShowAnalytics={setShowAnalytics}
        token={token}
      />

      <p style={{ marginTop: 20, color: '#a1a1aa', fontSize: '0.85rem', textAlign: 'center' }}>
        💡 Hover over markers to preview details. Click any marker to open the article in a new tab!
      </p>
      
      {/* Book Advertisements */}
      <MediumBookAd variant={2} />
      <BannerBookAd variant={3} />
    </div>
  );
};

export default MapPage;
